import { Fragment, type ReactNode } from "react";
import { Link } from "wouter";
import { icons } from "../company-ui";
import { ARTICLE_BY_ID } from "./content";
import { parseInline, type Block, type HelpArticle } from "./parse";

/**
 * One help article (ALN-53), from the mockup's `.helparticle`: lead, body, Related and Official
 * sources. In the drawer, links to other articles open them in the drawer (`onOpen`); on the help
 * center they are links. External links open in a new tab with rel="noopener noreferrer".
 */
export function Article({ article, level = 2, onOpen }: { article: HelpArticle; level?: 2 | 3; onOpen?: (id: string) => void }) {
  const related = article.related.filter(id => ARTICLE_BY_ID[id]);
  const H = (props: { sub?: boolean; children: ReactNode }) => {
    const l = Math.min(6, level + (props.sub ? 1 : 0));
    const Tag = `h${l}` as "h2";
    return <Tag className="helparticle__h">{props.children}</Tag>;
  };
  return (
    <div className="helparticle">
      <p className="lead">{inline(article.lead, onOpen)}</p>
      {article.body.map((b, i) => <BlockView key={i} b={b} H={H} onOpen={onOpen} />)}
      {related.length > 0 && (
        <>
          <H>Related</H>
          <div className="helplinks">
            {related.map(id => <ArticleLink key={id} id={id} onOpen={onOpen}>{ARTICLE_BY_ID[id]!.title}</ArticleLink>)}
          </div>
        </>
      )}
      {article.sources.length > 0 && (
        <>
          <H>Official sources</H>
          <div className="helplinks">
            {article.sources.map(s => <External key={s.href} href={s.href}>{s.label}</External>)}
          </div>
        </>
      )}
    </div>
  );
}

function BlockView({ b, H, onOpen }: { b: Block; H: (p: { sub?: boolean; children: ReactNode }) => JSX.Element; onOpen?: (id: string) => void }) {
  switch (b.t) {
    case "p": return <p>{inline(b.text, onOpen)}</p>;
    case "h": return <H sub={b.level === 3}>{inline(b.text, onOpen)}</H>;
    case "ul": return <ul>{b.items.map((t, i) => <li key={i}>{inline(t, onOpen)}</li>)}</ul>;
    case "ol": return <ol>{b.items.map((t, i) => <li key={i}>{inline(t, onOpen)}</li>)}</ol>;
    case "table": return (
      <div className="tablewrap" role="region" aria-label="Table" tabIndex={0}>
        <table className="t">
          <thead><tr>{b.head.map((h, i) => <th key={i} scope="col">{inline(h, onOpen)}</th>)}</tr></thead>
          <tbody>{b.rows.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j}>{inline(c, onOpen)}</td>)}</tr>)}</tbody>
        </table>
      </div>
    );
  }
}

function inline(s: string, onOpen?: (id: string) => void): ReactNode {
  return parseInline(s).map((n, i) => {
    switch (n.t) {
      case "b": return <b key={i}>{n.text}</b>;
      case "em": return <em key={i}>{n.text}</em>;
      case "code": return <code key={i} className="mono">{n.text}</code>;
      case "a": {
        if (/^https?:\/\//.test(n.href)) return <External key={i} href={n.href}>{n.text}</External>;
        const id = n.href.replace(/^\.\//, "").replace(/\.md$/, "");
        return ARTICLE_BY_ID[id] ? <ArticleLink key={i} id={id} onOpen={onOpen}>{n.text}</ArticleLink> : <Fragment key={i}>{n.text}</Fragment>;
      }
      default: return <Fragment key={i}>{n.text}</Fragment>;
    }
  });
}

export function ArticleLink({ id, onOpen, children }: { id: string; onOpen?: (id: string) => void; children: ReactNode }) {
  if (onOpen) return <button type="button" className="linkbtn" onClick={() => onOpen(id)}>{children}</button>;
  return <Link href={`/help/${id}`}>{children}</Link>;
}

function External({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}{icons.external}<span className="sr"> (opens in a new tab)</span>
    </a>
  );
}
